import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons/faCircleInfo";
import { Popover, Tooltip } from "../utils/local-antd";
import isFunction from "lodash/isFunction";
import React from "react";
import styled from "styled-components";

import i18n from "../i18n";
import { Rounded } from "../theme/Rounded";
import { Shadow } from "../theme/Shadow";
import { Spacing } from "../theme/Spacing";
import { Button } from "./Button";
import { Flex } from "./Flex";
import { FontSize, Text } from "./Text";
import { AtiraIcon } from "./AtiraIcon";
import { AppLangs } from "../model/shared/enum/AppLangs.enum";

const Container = styled(Flex)<{ removeSideShadow?: boolean }>`
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.white};
  margin-bottom: ${Spacing.m};
  padding: ${Spacing.m};
  width: 100%;
  /* Changing the hight here requires updating the '.ant-menu-item' class's 'height' in SideMenu.tsx */
  height: 3rem;
  box-shadow: ${Shadow.SM};
  position: sticky;
  top: 0;
  z-index: 100;

  ${({ removeSideShadow, theme }) =>
    removeSideShadow
      ? `
    box-shadow: ${
      i18n.language === AppLangs.AR
        ? "-0.125rem 0.25rem 0 rgba(0,0,0,0.075), 0 -0.125rem 0 rgba(0,0,0,0.075), 0 0.125rem 0 rgba(0,0,0,0.075)"
        : "0.125rem 0.25rem 0 rgba(0,0,0,0.075), 0 -0.125rem 0 rgba(0,0,0,0.075), 0 0.125rem 0 rgba(0,0,0,0.075)"
    };

    border-inline-start: 2px solid ${theme.sub};
  `
      : ""}
`;

const StyledButton = styled(Button)`
  background: ${({ theme }) => theme.lightMain};
  padding: ${Spacing.xs};
  border-radius: ${Rounded.lg};
  transition: border-bottom 0.3s;
  border: 1px solid ${({ theme }) => theme.transparent};
  transition: all 0.3s;
  font-size: calc(${FontSize.m} + 0.1rem);
  color: ${({ theme }) => theme.white};

  &:first-of-type {
    margin-inline-start: auto;
  }

  &:not(:last-of-type) {
    margin-inline-end: ${Spacing.s};
  }
`;

const PopoverButton = styled(Button)`
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.main};
  margin: 0;
  padding: 0;
`;

const StyledUl = styled.ul`
  list-style-position: inside;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  max-height: 20rem;
  max-width: 17rem;
  overflow-wrap: break-word;
  overflow-y: scroll;

  li::marker {
    color: ${({ theme }) => theme.main};
  }
`;

export type SubHeaderProps = {
  title: string | React.ReactNode;
  notes?: string[];
  icon: IconProp;
  removeSideShadow?: boolean;
  actions?: {
    title: string;
    icon: IconProp;
    onClick: VoidFunction;
    disabled?: boolean;
    hide?: boolean;
    testId?: string;
    tooltip?: string;
  }[];
  extra?: React.ReactNode;
};

export const SubHeader: React.FC<SubHeaderProps> = ({
  title,
  icon,
  notes = [],
  removeSideShadow = false,
  actions = [],
  extra,
}) => {
  return (
    <Container removeSideShadow={removeSideShadow}>
      <Flex gap="s" alignItems="center" flex={1} minWidth={0}>
        <Flex
          alignItems="center"
          gap="s"
          minWidth={0}
          className="atira-subheader-title-container"
        >
          <AtiraIcon icon={icon} size="xl" color="main" />

          <Text fontWeight="bold" fontSize="l" cutText>
            {title}
          </Text>
        </Flex>

        {notes.length ? (
          <Popover
            content={
              <StyledUl>
                {notes.map((x, i) => (
                  <li key={`${x}-${i}`}>{x}</li>
                ))}
              </StyledUl>
            }
            placement={
              i18n.language === AppLangs.AR ? "bottomLeft" : "bottomRight"
            }
            trigger={["hover", "click"]}
            getPopupContainer={(trigger) =>
              trigger.parentElement || document.body
            }
          >
            <PopoverButton icon={faCircleInfo} iconWidth="xl" />
          </Popover>
        ) : null}
      </Flex>

      <Flex flexShrink={0}>
        {extra}

        {actions
          .filter(
            (action) => action?.hide !== true && isFunction(action.onClick),
          )
          .map((action) => (
            <Tooltip title={action?.tooltip}>
              <StyledButton
                onClick={action.onClick}
                icon={action.icon}
                disabled={action?.disabled || false}
                color={action ? "black" : "gray"}
                dataTestId={action?.testId}
                hover
              >
                {action.title}
              </StyledButton>
            </Tooltip>
          ))}
      </Flex>
    </Container>
  );
};
